import React from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import './LeafletMap.css';
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from '../utils/mapTiles';

// Fix para o erro _leaflet_pos no react-leaflet v4
// Força o Leaflet a invalidar o cache de posições
function FixLeafletPos() {
  const map = useMap();
  React.useEffect(() => {
    // Fix imediato
    map.invalidateSize();

    // Fix após um delay para garantir que o DOM esteja pronto
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);

    // Fix após interação do usuário
    const handleResize = () => {
      map.invalidateSize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
}

export interface MapMarker {
  lat: number;
  lng: number;
  label?: string | number;
  color?: string;
}

export interface MapPolyline {
  positions: [number, number][];
  color?: string;
}

function numberedIcon(label: string | number | undefined, color: string) {
  return L.divIcon({
    className: 'leaflet-numbered-marker',
    html: `<div class="marker-pin" style="background:${color}"><span>${label ?? ''}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30]
  });
}

function FitBounds({ markers }: { markers: MapMarker[] }) {
  const map = useMap();
  React.useEffect(() => {
    if (markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [32, 32] });
  }, [markers, map]);
  return null;
}

function MapClickHandler({ onClick }: { onClick?: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      if (onClick) {
        onClick(e.latlng.lat, e.latlng.lng);
      }
    }
  });
  return null;
}

interface LeafletMapProps {
  markers: MapMarker[];
  polylines?: MapPolyline[];
  height?: string;
  defaultColor?: string;
  onMapClick?: (lat: number, lng: number) => void;
  interactive?: boolean;
}

const DEFAULT_CENTER: [number, number] = [-23.5505, -46.6333];

export default function LeafletMap({
  markers,
  polylines = [],
  height = '420px',
  defaultColor = '#2a9d8f',
  onMapClick,
  interactive = false
}: LeafletMapProps) {
  const center: [number, number] = markers.length > 0 ? [markers[0].lat, markers[0].lng] : DEFAULT_CENTER;

  return (
    <div style={{ height, borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
      <MapContainer
        center={center}
        zoom={12}
        style={{ height: height || '500px', width: '100%' }}
        zoomControl={false}
        zoomAnimation={false}
        fadeAnimation={false}
        markerZoomAnimation={false}
        className="leaflet-map-container"
      >
        <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} maxZoom={TILE_MAX_ZOOM} />
        <FixLeafletPos />
        {markers.map((m, i) => (
          <Marker key={`marker-${i}-${m.lat}-${m.lng}`} position={[m.lat, m.lng]} icon={numberedIcon(m.label, m.color || defaultColor)} />
        ))}
        {polylines.map((p, i) => (
          <Polyline key={`poly-${i}`} positions={p.positions} pathOptions={{ color: p.color || defaultColor, weight: 4 }} />
        ))}
        {interactive && <MapClickHandler onClick={onMapClick} />}
        {markers.length > 0 && <FitBounds markers={markers} />}
      </MapContainer>
    </div>
  );
}
